import React from 'react';
import { Avatar, Box, Text, UnstyledButton } from '@mantine/core';
import type { MantineSize } from '@mantine/core';
import { User } from '../../types';

const palette = ['#0F9981', '#C77514', '#5B6BCF', '#8A4FBF', '#B0413E', '#0B7CB8'];

export function userColor(name: string): string {
  const sum = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return palette[sum % palette.length];
}

export function userInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase();
}

type UserLike = Partial<
  Pick<User, 'name' | 'avatar' | 'position' | 'profession' | 'grade' | 'email'>
>;

export function getUserRole(u: UserLike | null | undefined): string | undefined {
  if (!u) return undefined;
  if (u.position) return u.position;
  const profession = u.profession;
  if (profession && typeof profession === 'object' && 'name' in profession) {
    return (profession as { name?: string }).name;
  }
  const grade = u.grade;
  if (grade && typeof grade === 'object' && 'title' in grade) {
    return (grade as { title?: string }).title;
  }
  return undefined;
}

const SIZE_MAP: Record<'xs' | 'sm' | 'md' | 'lg', number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
};

interface UserBadgeProps {
  user: UserLike | null | undefined;
  /** Px or preset. Default 'md' (40px). */
  size?: number | 'xs' | 'sm' | 'md' | 'lg';
  /**
   * What to show under the name.
   * - `'role'` (default) — position / profession.name / grade.title
   * - `'email'` — user.email
   * - string — custom text
   * - `null` — hide subtitle row
   */
  subtitle?: 'role' | 'email' | string | null;
  /** Optional click handler (renders as button). */
  onClick?: () => void;
  /** Truncate long name/subtitle on a single line. Default true. */
  truncate?: boolean;
  /** Fallback name when `user` is missing/empty. */
  fallbackName?: string;
  /** Hide the text block and render only the avatar. */
  avatarOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const nameFontSize = (px: number): MantineSize | number => {
  if (px <= 24) return 'xs';
  if (px <= 32) return 'sm';
  if (px <= 40) return 'sm';
  return 'md';
};

const subtitleFontSize = (px: number): MantineSize => (px <= 32 ? 'xs' : 'xs');

export const UserBadge: React.FC<UserBadgeProps> = ({
  user,
  size = 'md',
  subtitle = 'role',
  onClick,
  truncate = true,
  fallbackName,
  avatarOnly,
  className,
  style,
}) => {
  const px = typeof size === 'number' ? size : SIZE_MAP[size];
  const name = user?.name || fallbackName || '—';
  const avatarSrc = user?.avatar || undefined;

  const subtitleText =
    subtitle === null
      ? null
      : subtitle === 'role'
        ? getUserRole(user)
        : subtitle === 'email'
          ? user?.email
          : subtitle;

  const truncStyle: React.CSSProperties = truncate
    ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
    : {};

  const avatarNode = (
    <Avatar
      src={avatarSrc}
      size={px}
      radius="xl"
      name={name}
      // No-image fallback uses our deterministic palette: colored background +
      // white initials. Styling the `placeholder` element (not the root) is what
      // makes the initials text color apply — Mantine's `color="initials"`
      // otherwise leaves the text its own auto-generated (clashing) colour.
      styles={{ placeholder: { backgroundColor: userColor(name), color: '#fff' } }}
      style={{ flexShrink: 0 }}
    >
      {userInitials(name)}
    </Avatar>
  );

  const content = (
    <Box
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: px <= 28 ? 8 : 12,
        minWidth: 0,
        ...style,
      }}
      className={className}
    >
      {avatarNode}
      {!avatarOnly && (
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size={nameFontSize(px) as MantineSize} fw={600} style={truncStyle}>
            {name}
          </Text>
          {subtitleText && (
            <Text size={subtitleFontSize(px)} c="dimmed" style={{ marginTop: 2, ...truncStyle }}>
              {subtitleText}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );

  if (onClick) {
    return (
      <UnstyledButton onClick={onClick} style={{ display: 'inline-flex', minWidth: 0 }}>
        {content}
      </UnstyledButton>
    );
  }
  return content;
};
